import { FileWithURL } from 'bff';
import { UpdateNote, UserType } from 'database';
import { providerUserTypes } from 'piramid-constants';
import { Avatar, AvatarFallback, AvatarImage, cn, ScrollArea } from 'ui';
import es from 'date-fns/locale/es';
import { customFormatDistance, getHumanFileSize } from 'utils';
import { FileCardIcon } from './FileCard';
import { IconMailExclamation } from '@tabler/icons-react';

interface UpdateNoteMinimal
  extends Pick<UpdateNote, 'id' | 'message' | 'created_at'> {
  files: FileWithURL[];
  published_by: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    profile_photo: string | null;
    // Only the order thread selects it; the contract/appraisal lists render
    // with `layout='default'`, which never reads it.
    type?: UserType;
  };
}

type Author = UpdateNoteMinimal['published_by'];

// 'default' keeps the activity-style list; 'chat' renders the thread as a
// two-sided conversation (own side right, the other party's left).
export type UpdateNotesListLayout = 'default' | 'chat';

type ThreadSide = 'provider' | 'insurer';

// The thread has exactly two parties, and each one can have several senders
// (e.g. an insurer admin and the order's handler), so sides are resolved by
// user type — never by author identity.
const sideOf = (type: UserType): ThreadSide =>
  providerUserTypes.some((providerType) => providerType === type)
    ? 'provider'
    : 'insurer';

// Falls back to "not mine" (left) when the side can't be resolved, so an
// unknown author never lands on the viewer's side by accident.
const isOwnMessage = (author: Author, ownSide?: ThreadSide): boolean =>
  !!author.type && !!ownSide && sideOf(author.type) === ownSide;

const authorName = (author: Author): string => {
  const full = `${author.first_name ?? ''} ${author.last_name ?? ''}`.trim();
  return full;
};

const authorInitials = (author: Author): string => {
  const initials = `${author.first_name?.charAt(0) ?? ''}${
    author.last_name?.charAt(0) ?? ''
  }`.trim();
  return initials || author.email.charAt(0).toUpperCase();
};

const UpdateNoteAttachment = ({ document }: { document: FileWithURL }) => (
  <a className='group' target='_blank' href={document.url}>
    <div className='flex items-center gap-0.5 px-1.5 py-0.5'>
      <FileCardIcon className='w-3.5 h-3.5' mimetype={document.mimetype!} />
      <span className='text-xs hover:underline'>{document.original_name}</span>
      <span className='text-xs text-muted-foreground'>
        ({getHumanFileSize(Number(document.size), true)})
      </span>
    </div>
  </a>
);

const UpdateNoteRow = ({
  note,
  isFirst,
}: {
  note: UpdateNoteMinimal;
  isFirst: boolean;
}) => (
  <div
    className={cn('flex flex-row items-start justify-between gap-1', {
      'm-1': !isFirst,
    })}
  >
    <div className='flex flex-row items-start space-x-2 w-full md:w-5/6 mt-2'>
      <Avatar size='sm' className='mt-1'>
        <AvatarImage
          src={
            note.published_by.profile_photo ||
            '/images/small-user-placeholder.png'
          }
        />
        <AvatarFallback className='text-xs'>
          {authorInitials(note.published_by)}
        </AvatarFallback>
      </Avatar>
      <div className='flex flex-col min-w-0 flex-1 gap-0'>
        <span className='font-semibold text-base_brand text-foreground'>
          {authorName(note.published_by)}
        </span>
        <span className='text-xs text-stone-400 text-foreground'>
          {note.published_by.email ?? ''}
        </span>
        <p className='text-[13px] text-muted-foreground whitespace-pre-wrap mt-2 font-medium'>
          {note.message}
        </p>
        {note.files.length > 0 && (
          <div className='flex flex-row flex-wrap gap-1 mt-1'>
            {note.files.map((document) => (
              <UpdateNoteAttachment key={document.id} document={document} />
            ))}
          </div>
        )}
      </div>
    </div>

    <div className='text-xs flex flex-col items-end text-foreground-secondary shrink-0 mt-2 pr-1'>
      <span className='text-end'>
        {customFormatDistance(new Date(), new Date(note.created_at), {
          locale: es,
          justDate: true,
        })}
      </span>
      <span className='text-stone-400 text-end'>
        {customFormatDistance(new Date(), new Date(note.created_at), {
          locale: es,
          justSuffix: true,
        })}
      </span>
    </div>
  </div>
);

const ChatMessage = ({
  note,
  isOwn,
}: {
  note: UpdateNoteMinimal;
  isOwn: boolean;
}) => (
  <div
    className={cn(
      'flex items-end gap-2',
      isOwn ? 'flex-row-reverse' : 'flex-row',
    )}
  >
    <Avatar className='shrink-0 mb-1'>
      <AvatarImage
        src={
          note.published_by.profile_photo ||
          '/images/small-user-placeholder.png'
        }
      />
      <AvatarFallback className='text-xs'>
        {authorInitials(note.published_by)}
      </AvatarFallback>
    </Avatar>

    <div
      className={cn(
        'flex flex-col min-w-0 max-w-[85%] md:max-w-[70%] rounded-2xl px-3 py-2 text-foreground',
        isOwn ? 'bg-stone-100 rounded-br-sm' : 'bg-stone-100 rounded-bl-sm',
      )}
    >
      {/* Shown on both sides: each side can have more than one sender, so the
          author always has to be identifiable. */}
      <span className='text-xs font-semibold truncate'>
        {authorName(note.published_by) || note.published_by.email}
      </span>

      <p className='text-[13px] whitespace-pre-wrap break-words py-0.5'>
        {note.message}
      </p>

      {note.files.length > 0 && (
        <div className='flex flex-col gap-0.5 mt-1 pt-1 border-t border-stone-300'>
          {note.files.map((document) => (
            <UpdateNoteAttachment key={document.id} document={document} />
          ))}
        </div>
      )}

      <span className='text-[10px] self-end mt-1 text-muted-foreground'>
        {customFormatDistance(new Date(), new Date(note.created_at), {
          locale: es,
          justDate: true,
        })}
      </span>
    </div>
  </div>
);

// Mirrors the activity item layout: avatar + name on the left, message and
// attachments below it, and the time on the right. Wrapped in a scroll area
// pinned to the bottom (newest novedad in view), like ActivityEventsList.
export const UpdateNotesList = ({
  updateNotes,
  className,
  layout = 'default',
  currentUserType,
  composer,
}: {
  updateNotes: UpdateNoteMinimal[];
  className?: string;
  layout?: UpdateNotesListLayout;
  // The viewer's user type: its side goes right, the other party's left. Under
  // `layout='chat'` without it every message renders as incoming.
  currentUserType?: UserType;
  // Message box pinned under the thread. Only rendered by `layout='chat'`; the
  // app injects it because the submit endpoint differs per side.
  composer?: React.ReactNode;
}) => {
  const ownSide = currentUserType ? sideOf(currentUserType) : undefined;

  return (
    <div className='bg-white'>
      {/* <h3 className='text-lg font-semibold m-2'>Novedades de la orden</h3> */}
      {updateNotes.length > 0 ? (
        <ScrollArea
          orientation='vertical'
          className={cn('flex flex-col max-h-[60vh] w-full', className)}
          scrollToBottom
        >
          {layout === 'chat' ? (
            <div className='flex flex-col gap-3 px-3 py-2'>
              {updateNotes.map((note) => (
                <ChatMessage
                  key={note.id}
                  note={note}
                  isOwn={isOwnMessage(note.published_by, ownSide)}
                />
              ))}
            </div>
          ) : (
            <div className='flex flex-col px-3'>
              {updateNotes.map((note, index) => (
                <UpdateNoteRow
                  key={note.id}
                  note={note}
                  isFirst={index === 0}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      ) : (
        <div className='text-center text-sm text-muted-foreground flex items-center justify-center gap-2 py-4'>
          <IconMailExclamation className='w-4 h-4' />
          <span>No se encontraron mensajes. Puedes enviar uno nuevo.</span>
        </div>
      )}

      {layout === 'chat' && composer}
    </div>
  );
};
